/** @jsxImportSource @emotion/react */
"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import InstagramIcon from "@mui/icons-material/Instagram";
import StorefrontIcon from "@mui/icons-material/Storefront";
import FamentoLogo from "@/assets/FamentoLogo";
import { theme } from "@/constants/theme";
import { famentoShopierAdd } from "@/constants/texts/contact";

import {
  navbarStyles,
  toolbarStyles,
  drawerStyles,
  menuButtonStyles,
  navItemsStyles,
  buttonStyles,
  logoStyles,
  drawerFooterStyles,
} from "./styles";

const Navbar = () => {
  const pathname = usePathname();
  const isHome = pathname === "/";

  const [homeScrollPastThreshold, setHomeScrollPastThreshold] = useState(false);
  const scrolled = !isHome || homeScrollPastThreshold;
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isHome) {
      return;
    }

    window.scrollTo(0, 0);
    queueMicrotask(() => {
      setHomeScrollPastThreshold(false);
    });

    const handleScroll = () => {
      setHomeScrollPastThreshold(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  const navItems = [
    { label: "Anasayfa", href: "/" },
    { label: "Hakkımızda", href: "/hakkimizda" },
    { label: "Ürünler", href: "/urunler" },
    { label: "Blog", href: "/blog" },
    { label: "İletişim", href: "/iletisim" },
  ];

  const drawer = (
    <Box css={drawerStyles}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mx={2}
        mt={2}
      >
        <FamentoLogo width={160} height={60} />
        <IconButton aria-label="close" onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        height="calc(100% - 80px)"
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <List>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton
                component={Link}
                href={item.href}
                onClick={handleDrawerToggle}
                sx={{
                  minHeight: 48,
                  display: "flex",
                  justifyContent: "center",
                  px: 2,
                }}
              >
                <ListItemText
                  primary={item.label}
                  sx={{ textAlign: "center" }}
                />
              </ListItemButton>
            </ListItem>
          ))}
          {/* Mobile Shopier Button */}
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              href={famentoShopierAdd}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleDrawerToggle}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <StorefrontIcon sx={{ fontSize: 20 }} />
              <Typography>Mağazaya Git</Typography>
            </ListItemButton>
          </ListItem>
        </List>
        <Box css={drawerFooterStyles}>
          <Link
            href="https://instagram.com/famentodesign"
            target="_blank"
            rel="noopener noreferrer"
          >
            <InstagramIcon sx={{ fontSize: 30 }} />
            <Typography fontSize={14}>Famento Design</Typography>
          </Link>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar component="nav" css={navbarStyles(scrolled)}>
        <Toolbar css={toolbarStyles()}>
          <IconButton
            size="large"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            css={menuButtonStyles}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Link
            aria-label="Home"
            href="/"
            style={{ display: "flex", alignItems: "center" }}
          >
            <FamentoLogo
              color={scrolled ? theme.palette.primary.main : "white"}
              width={180}
              height={60}
              css={logoStyles(scrolled)}
            />
          </Link>

          <Box css={navItemsStyles}>
            {navItems.map((item) => (
              <Button
                key={item.label}
                component={Link}
                href={item.href}
                size="small"
                disableRipple
                css={buttonStyles}
                sx={{ mx: 1 }}
              >
                {item.label}
              </Button>
            ))}

            {/* Mini Shopier Button */}
            <Button
              href={famentoShopierAdd}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              disableRipple
              sx={{ textTransform: "none" }}
              variant="contained"
              startIcon={<StorefrontIcon fontSize="small" />}
            >
              Mağazaya Git
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        transitionDuration={500}
        open={mobileOpen}
        css={drawerStyles}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
